import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, X, Loader2, ImageOff } from 'lucide-react';
import { BlobImage } from './BlobImage';
import { wl } from './theme';

interface Props {
  assetIds: string[];
  label: string;
  /** Resolves with the newly-created asset_id so its local browser-side
   * preview can be matched up to the right thumbnail. */
  onUpload: (file: File) => Promise<string>;
  onDelete: (assetId: string) => Promise<void>;
  /** Optional — when the caller's backend can serve this asset's raw bytes
   * back (currently brand reference logos only, not VIP reference photos),
   * pass the fetch path for a given assetId and a real thumbnail replaces
   * the generic placeholder icon once the upload-time browser preview
   * (which only lasts until the next page load) is gone. */
  getImagePath?: (assetId: string) => string;
}

// Shared dropzone + thumbnail grid for both VIP reference photos and brand
// reference logos — same upload UX, different endpoint (parameterized via
// onUpload/onDelete rather than two near-duplicate components).
export function ReferencePhotoUpload({ assetIds, label, onUpload, onDelete, getImagePath }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    setUploading(true);
    try {
      const reader = new FileReader();
      const dataUrlPromise = new Promise<string>((resolve) => {
        reader.onload = () => resolve(String(reader.result));
      });
      reader.readAsDataURL(file);
      const [newAssetId, dataUrl] = await Promise.all([onUpload(file), dataUrlPromise]);
      setPreviews((p) => ({ ...p, [newAssetId]: dataUrl }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function handleDelete(assetId: string) {
    setDeletingId(assetId);
    try {
      await onDelete(assetId);
      setPreviews((p) => { const next = { ...p }; delete next[assetId]; return next; });
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3">
        {assetIds.map((id) => (
          <div key={id} className="relative" style={{ width: 72, height: 72 }}>
            <div
              style={{ width: 72, height: 72, borderRadius: 8, background: wl.bg, border: `1px solid ${wl.border}`, overflow: 'hidden' }}
              className="flex items-center justify-center"
            >
              {previews[id] ? (
                <img src={previews[id]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : getImagePath ? (
                <BlobImage path={getImagePath(id)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <ImageOff size={18} style={{ color: wl.textFaint }} />
              )}
            </div>
            <button
              onClick={() => handleDelete(id)}
              disabled={deletingId === id}
              className="absolute -top-1.5 -right-1.5 rounded-full flex items-center justify-center"
              style={{ width: 20, height: 20, background: wl.critical, color: 'white' }}
            >
              {deletingId === id ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />}
            </button>
          </div>
        ))}

        <motion.button
          whileHover={{ borderColor: `${wl.green}60` }}
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          style={{ width: 72, height: 72, borderRadius: 8, border: `1.5px dashed ${wl.border}`, background: wl.bg }}
          className="flex items-center justify-center flex-shrink-0"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" style={{ color: wl.green }} /> : <Upload size={16} style={{ color: wl.textMuted }} />}
        </motion.button>
      </div>
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
      <p style={{ color: wl.textMuted, fontSize: 11, fontFamily: wl.font }}>{label} — {assetIds.length} on file</p>
      {error && <p style={{ color: wl.critical, fontSize: 11.5, marginTop: 4, fontFamily: wl.font }}>{error}</p>}
    </div>
  );
}
